import React from "react";
import { Link } from "react-router-dom";
import { UploadCloud, ShieldCheck, ArrowUpRight } from "lucide-react";
import { COMPANY, DISCLAIMERS } from "@/lib/compliance";
import EmailContact from "@/components/EmailContact";
import VisitCounter from "@/components/VisitCounter";
import SecureUploadDialog from "@/components/SecureUploadDialog";
import { SERVICES } from "@/lib/services";

const POLICY_LINKS = [
    { to: "/leadership", label: "About / Leadership" },
    { to: "/privacy", label: "Privacy Notice" },
    { to: "/terms", label: "Terms of Service" },
    { to: "/licensing", label: "Licensing & Disclosures" },
    { to: "/accessibility", label: "Accessibility" },
    { to: "/contact", label: "Contact" },
];

const BLOG_LINKS = [
    { to: "/blog", label: "Agency Blog (All Topics)" },
    { to: "/blog/category/health", label: "Medicare & Health Articles" },
    { to: "/blog/category/personal", label: "Personal Insurance Articles" },
    { to: "/blog/category/business", label: "Business Insurance Articles" },
    { to: "/blog/category/special-risk", label: "Special Risk Articles" },
];

const DROPBOX_FILE_REQUEST_URL =
    "https://www.dropbox.com/request/stf7wjjkayksmvh2d17q";

export default function FooterBar() {
    return (
        <footer
            data-testid="footer"
            className="relative border-t border-[rgba(212,175,55,0.18)] bg-[#05070a]"
            role="contentinfo"
        >
            {/* Secure Document Upload band — Dropbox File Request */}
            <section
                data-testid="footer-document-upload"
                aria-labelledby="footer-document-upload-heading"
                className="border-b border-[rgba(212,175,55,0.12)] bg-gradient-to-b from-[#0b0e13] to-[#05070a]"
            >
                <div className="mx-auto flex max-w-[1400px] flex-col items-start gap-6 px-6 py-10 md:flex-row md:items-center md:justify-between md:gap-10 md:px-10 md:py-12">
                    <div className="flex items-start gap-4 md:items-center">
                        <span
                            aria-hidden="true"
                            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[rgba(212,175,55,0.4)] bg-[#0d1117]"
                        >
                            <UploadCloud className="h-5 w-5 text-brand-gold2" />
                        </span>
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft">
                                Secure Document Upload
                            </p>
                            <h2
                                id="footer-document-upload-heading"
                                className="mt-2 font-serif text-2xl leading-tight text-brand-ivory md:text-3xl"
                            >
                                Endurance Casualty Secure Document Upload
                            </h2>
                            <p
                                data-testid="footer-document-upload-helper"
                                className="mt-2 max-w-xl text-sm leading-relaxed text-brand-ivory/85"
                            >
                                Upload your documents securely through Dropbox.
                            </p>
                        </div>
                    </div>

                    <div className="flex w-full flex-col items-start gap-3 md:w-auto md:items-end">
                        <SecureUploadDialog
                            dropboxUrl={DROPBOX_FILE_REQUEST_URL}
                            trigger={
                                <button
                                    type="button"
                                    data-testid="footer-document-upload-button"
                                    aria-label="Submit documents securely — review disclosure first"
                                    className="group inline-flex w-full items-center justify-center gap-3 rounded-full border border-brand-gold bg-brand-gold px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.2em] text-brand-bg shadow-[0_18px_36px_-18px_rgba(212,175,55,0.6)] transition-all hover:bg-brand-gold2 hover:shadow-[0_22px_44px_-18px_rgba(212,175,55,0.75)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg active:scale-[0.98] md:w-auto"
                                >
                                    <ShieldCheck
                                        aria-hidden="true"
                                        className="h-4 w-4"
                                    />
                                    <span>Submit Documents Securely</span>
                                    <ArrowUpRight
                                        aria-hidden="true"
                                        className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                    />
                                </button>
                            }
                        />
                        <p
                            data-testid="footer-document-upload-privacy"
                            className="max-w-md text-xs leading-relaxed text-brand-muted md:text-right"
                        >
                            Files are submitted through Dropbox and delivered
                            directly to Endurance Casualty&apos;s secure intake
                            folder. Uploaded documents are not publicly
                            accessible and cannot be viewed by other users.
                        </p>
                    </div>
                </div>
            </section>

            {/* Top — business identity + links */}
            <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-6 py-12 md:grid-cols-5 md:gap-8 md:px-10 md:py-14">
                {/* Identity */}
                <div>
                    <p className="font-serif text-xl text-brand-ivory">
                        {COMPANY.legalName}
                    </p>
                    <p
                        data-testid="footer-address"
                        className="mt-3 text-sm text-brand-muted"
                    >
                        {COMPANY.address}
                    </p>
                    <p className="mt-3 text-sm text-brand-muted">
                        Phone:{" "}
                        <a
                            data-testid="footer-phone"
                            href={`tel:${COMPANY.phoneTel}`}
                            className="text-brand-ivory underline-offset-4 hover:text-brand-gold2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                        >
                            {COMPANY.phone}
                        </a>
                    </p>
                    <p className="mt-1 text-sm text-brand-muted">
                        Email:{" "}
                        <EmailContact variant="footer" testIdPrefix="footer-email" />
                    </p>
                </div>

                {/* Solutions */}
                <nav
                    aria-label="Insurance solutions"
                    data-testid="footer-solutions-nav"
                >
                    <p className="text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft">
                        Solutions
                    </p>
                    <ul className="mt-4 space-y-2 text-sm">
                        {SERVICES.map((s) => (
                            <li key={s.slug}>
                                <Link
                                    to={s.path}
                                    data-testid={`footer-solution-${s.slug}`}
                                    className="text-brand-ivory/85 underline-offset-4 hover:text-brand-gold2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                                >
                                    {s.title}
                                </Link>
                            </li>
                        ))}
                        <li>
                            <Link
                                to="/insurance-education-videos"
                                data-testid="footer-link-videos"
                                className="text-brand-ivory/85 underline-offset-4 hover:text-brand-gold2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                            >
                                Education Video Library
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/pay-premium"
                                data-testid="footer-link-pay-premium"
                                className="font-medium text-brand-gold2 underline-offset-4 hover:underline hover:text-brand-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                            >
                                Account → Pay Premium
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/insurance-solutions"
                                data-testid="footer-solution-hub"
                                className="mt-1 inline-block text-xs uppercase tracking-[0.25em] text-brand-goldSoft hover:text-brand-gold2"
                            >
                                View all →
                            </Link>
                        </li>
                    </ul>
                </nav>

                {/* Resources — blog + education (gives blog categories real anchor text and
                    eliminates the orphaned-sitemap-page warnings) */}
                <nav
                    aria-label="Resources and articles"
                    data-testid="footer-resources-nav"
                >
                    <p className="text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft">
                        Resources
                    </p>
                    <ul className="mt-4 space-y-2 text-sm">
                        {BLOG_LINKS.map(({ to, label }) => (
                            <li key={to}>
                                <Link
                                    to={to}
                                    data-testid={`footer-link-${to.replace(/\//g, "-").replace(/^-/, "")}`}
                                    className="text-brand-ivory/85 underline-offset-4 hover:text-brand-gold2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                                >
                                    {label}
                                </Link>
                            </li>
                        ))}
                        <li>
                            <Link
                                to="/insurance-education-videos"
                                data-testid="footer-link-education-videos"
                                className="text-brand-ivory/85 underline-offset-4 hover:text-brand-gold2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                            >
                                Video Library
                            </Link>
                        </li>
                    </ul>
                </nav>

                {/* Policies */}
                <nav
                    aria-label="Policy and disclosure links"
                    data-testid="footer-policy-nav"
                >
                    <p className="text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft">
                        Policies & Disclosures
                    </p>
                    <ul className="mt-4 space-y-2 text-sm">
                        {POLICY_LINKS.map(({ to, label }) => (
                            <li key={to}>
                                <Link
                                    to={to}
                                    data-testid={`footer-link-${to.replace("/", "")}`}
                                    className="text-brand-ivory/85 underline-offset-4 hover:text-brand-gold2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                                >
                                    {label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                {/* Important notices (long-form) */}
                <div data-testid="footer-notices">
                    <p className="text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft">
                        Important Notices
                    </p>
                    <p
                        data-testid="footer-general-disclosure"
                        className="mt-4 text-xs leading-relaxed text-brand-muted"
                    >
                        Information on this website is general in nature and is
                        provided for introductory purposes only. Products, services,
                        materials, timing, follow-up, and next steps are subject to
                        availability, which may change. Submitting a request through
                        this website does not create coverage, bind insurance,
                        guarantee eligibility, or guarantee any particular result.
                    </p>
                    <p
                        data-testid="footer-state-scope"
                        className="mt-4 text-xs leading-relaxed text-brand-muted"
                    >
                        {DISCLAIMERS.stateScope}
                    </p>
                    <p
                        data-testid="footer-not-insurer"
                        className="mt-4 text-xs leading-relaxed text-brand-muted"
                    >
                        {DISCLAIMERS.notInsurer}
                    </p>
                    <p
                        data-testid="footer-aml-notice"
                        className="mt-4 text-xs leading-relaxed text-brand-muted"
                    >
                        <span className="text-brand-goldSoft">
                            Anti-Money Laundering Notice:{" "}
                        </span>
                        Where applicable, Endurance Casualty may request
                        information reasonably necessary to support identity
                        verification, transaction review, carrier requirements,
                        financial institution procedures, or applicable
                        anti-money laundering obligations. Endurance Casualty
                        may be unable to proceed with a request if required
                        information is not provided or if a transaction cannot
                        be appropriately reviewed. This notice is general and
                        does not create coverage, service, approval,
                        eligibility, or legal advice.
                    </p>

                    {/* Disability Access — accommodation availability */}
                    <section
                        data-testid="footer-disability-access"
                        aria-labelledby="footer-disability-access-heading"
                        className="mt-6 border-t border-[rgba(212,175,55,0.15)] pt-5"
                    >
                        <h3
                            id="footer-disability-access-heading"
                            className="text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft"
                        >
                            Disability Access
                        </h3>
                        <p
                            data-testid="footer-disability-access-body"
                            className="mt-4 text-xs leading-relaxed text-brand-muted"
                        >
                            Endurance Casualty is committed to making our
                            website and insurance information accessible to
                            individuals with disabilities. If you need
                            assistance using this website, require an
                            accommodation, or would like information in an
                            alternate format, please contact us at{" "}
                            <a
                                data-testid="footer-disability-access-phone"
                                href={`tel:${COMPANY.phoneTel}`}
                                className="text-brand-ivory underline-offset-4 hover:text-brand-gold2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                            >
                                {COMPANY.phone}
                            </a>{" "}
                            or{" "}
                            <a
                                data-testid="footer-disability-access-email"
                                href={`mailto:${COMPANY.email}`}
                                className="text-brand-ivory underline-offset-4 hover:text-brand-gold2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                            >
                                {COMPANY.email}
                            </a>
                            .
                        </p>
                        <Link
                            to="/accessibility"
                            data-testid="footer-disability-access-link"
                            className="mt-3 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.25em] text-brand-gold2 underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                        >
                            Read our full Accessibility Statement →
                        </Link>
                    </section>
                </div>
            </div>

            {/* Bottom strip */}
            <div className="border-t border-[rgba(212,175,55,0.12)]">
                <div className="mx-auto flex max-w-[1400px] flex-col items-center justify-between gap-2 px-6 py-6 text-center text-xs text-brand-muted md:flex-row md:px-10 md:text-left">
                    <span>
                        © {new Date().getFullYear()} {COMPANY.fullLegalName}.
                        All rights reserved.
                    </span>
                    <VisitCounter />
                    <span className="tracking-[0.25em] uppercase">
                        Call {COMPANY.phone}
                    </span>
                </div>
            </div>
        </footer>
    );
}
